import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Truck, Coins, CreditCard, FileImage } from 'lucide-react';
import { API } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/ImageUpload';
import { MobileNav } from '../components/MobileNav';
import { RegionSelect } from '../components/RegionSelect';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

const inputStyle = {
  background: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#F8FAF9'
};

const PAYMENT_METHODS = [
  { id: 'coins', label: 'Coins', icon: Coins, desc: 'Deducted instantly' },
  { id: 'qris', label: 'QRIS', icon: CreditCard, desc: 'Upload proof after pay' },
  { id: 'mandiri', label: 'Mandiri', icon: CreditCard, desc: 'Upload proof after pay' },
  { id: 'pending_admin_quote', label: 'Admin Quote', icon: Truck, desc: 'Admin akan tetapkan ongkir' },
];

export const KujiShippingRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const inventoryIds = location.state?.inventoryIds || [];

  const [inventoryDetail, setInventoryDetail] = useState([]);
  const [rates, setRates] = useState([]);
  const [rateMeta, setRateMeta] = useState({ source: '', requires_manual_override: false, message: '' });
  const [originInfo, setOriginInfo] = useState({ city: '', district: '' });
  const [loadingRates, setLoadingRates] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/shipping/origin`)
      .then((r) => {
        if (r.data?.configured) setOriginInfo({ city: r.data.city, district: r.data.district });
      })
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    recipient_name: '',
    recipient_phone: '',
    address_line: '',
    province: '',
    city: '',
    district: '',
    postal_code: '',
    courier: 'jne',
    service: 'REG',
    shipping_cost: 0,
    payment_method: 'coins',
    payment_proof_url: '',
    notes: '',
  });

  useEffect(() => {
    if (inventoryIds.length === 0) {
      toast.error('No items selected — go back to inventory');
      navigate('/kuji/inventory');
      return;
    }
    axios.get(`${API}/kuji/inventory`, { withCredentials: true })
      .then((r) => {
        const items = (r.data || []).filter((i) => inventoryIds.includes(i.id));
        setInventoryDetail(items);
      })
      .catch(() => toast.error('Failed to load selected items'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryIds.join(','), navigate]);

  // Total weight (grams) for the selected items
  const totalWeightGrams = inventoryDetail.reduce(
    (sum, it) => sum + (parseInt(it.weight_grams, 10) || 0),
    0
  );

  const refreshRates = useCallback(async () => {
    if (!form.province || !form.city || !form.district) { setRates([]); setRateMeta({ source:'', requires_manual_override:false, message:'' }); return; }
    if (!totalWeightGrams || totalWeightGrams <= 0) { setRates([]); setRateMeta({ source:'', requires_manual_override:false, message:'' }); return; }
    setLoadingRates(true);
    try {
      const { data } = await axios.get(`${API}/shipping/rates`, {
        params: {
          province: form.province, city: form.city, district: form.district,
          courier: form.courier, weight_grams: totalWeightGrams,
        }
      });
      const services = data?.services || [];
      setRates(services);
      setRateMeta({
        source: data?.source || '',
        requires_manual_override: !!data?.requires_manual_override,
        message: data?.message || '',
      });
      if (data?.requires_manual_override) {
        // Auto-switch to pending-quote payment method
        setForm((f) => ({ ...f, payment_method: 'pending_admin_quote', shipping_cost: 0, service: '' }));
        return;
      }
      const match = services.find((r) => r.service === form.service);
      if (match) setForm((f) => ({ ...f, shipping_cost: match.cost }));
      else if (services.length > 0) setForm((f) => ({ ...f, service: services[0].service, shipping_cost: services[0].cost }));
    } catch (e) {
      setRates([]);
      const detail = e.response?.data?.detail;
      if (detail) toast.error(detail);
    } finally {
      setLoadingRates(false);
    }
  }, [form.province, form.city, form.district, form.courier, form.service, totalWeightGrams]);

  useEffect(() => { refreshRates(); }, [refreshRates]);

  const submit = async (e) => {
    e.preventDefault();
    if (inventoryIds.length === 0) return;
    if (!form.recipient_name || !form.recipient_phone || !form.address_line || !form.province || !form.city || !form.district) {
      toast.error('Mohon lengkapi alamat (provinsi, kota, kecamatan)');
      return;
    }
    if (form.payment_method !== 'pending_admin_quote' && form.shipping_cost <= 0) {
      toast.error('Pilih layanan pengiriman untuk mendapat tarif');
      return;
    }
    if ((form.payment_method === 'qris' || form.payment_method === 'mandiri') && !form.payment_proof_url) {
      toast.error('Upload bukti pembayaran untuk QRIS / Mandiri');
      return;
    }
    if (form.payment_method === 'coins' && (user?.coin_balance || 0) < form.shipping_cost) {
      toast.error('Saldo coin tidak cukup untuk ongkir ini');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { inventory_ids: inventoryIds, ...form };
      const { data } = await axios.post(`${API}/kuji/shipping/requests`, payload, { withCredentials: true });
      toast.success(form.payment_method === 'pending_admin_quote'
        ? 'Permintaan diteruskan ke admin untuk penetapan ongkir manual'
        : 'Permintaan pengiriman dibuat');
      if (form.payment_method === 'coins') await checkAuth();
      navigate(`/kuji/shipping/${data.id}`);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Gagal membuat permintaan');
    } finally {
      setSubmitting(false);
    }
  };

  const totalValue = inventoryDetail.reduce((s, i) => s + (i.estimated_value_idr || 0), 0);

  return (
    <div className="min-h-screen pb-40" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="shipping-request-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/kuji/inventory')} className="flex items-center gap-2" data-testid="shipping-back">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
            <span style={{ color: '#F8FAF9' }}>Inventory</span>
          </button>
          <h1 className="text-xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>New Shipment</h1>
          <span className="w-12" />
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-6 space-y-6">
        {/* Items summary */}
        <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="shipping-items-summary">
          <h2 className="text-sm uppercase tracking-wider mb-3" style={{ color: '#D4AF37' }}>Items in this shipment</h2>
          <div className="space-y-2">
            {inventoryDetail.map((it) => (
              <div key={it.id} className="flex items-center gap-3">
                <img src={it.prize_image_snapshot} alt={it.prize_name_snapshot} className="w-12 h-12 rounded-lg object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{it.prize_name_snapshot}</p>
                  <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{it.event_name_snapshot}</p>
                </div>
                <span className="text-xs" style={{ color: '#A3B8AC' }}>{fmt(it.estimated_value_idr)} IDR</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="text-sm" style={{ color: '#A3B8AC' }}>Total value</span>
            <span className="text-sm font-semibold" style={{ color: '#D4AF37' }}>{fmt(totalValue)} IDR</span>
          </div>
        </section>

        {/* Recipient & Address */}
        <section className="rounded-2xl p-5 space-y-3 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-sm uppercase tracking-wider" style={{ color: '#D4AF37' }}>Recipient</h2>
          <input type="text" placeholder="Recipient name" required value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="recipient-name-input" />
          <input type="tel" placeholder="Phone (+628...)" required value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="recipient-phone-input" />
          <textarea placeholder="Full address" required rows="3" value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="recipient-address-input" />
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#A3B8AC' }}>Alamat Wilayah (Provinsi · Kota · Kecamatan)</label>
            <RegionSelect
              value={{ province: form.province, city: form.city, district: form.district }}
              onChange={(next) => setForm({ ...form, province: next.province, city: next.city, district: next.district })}
              required
            />
          </div>
          <input type="text" placeholder="Postal code (optional)" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} data-testid="postal-code-input" />
        </section>

        {/* Courier & Service */}
        <section className="rounded-2xl p-5 space-y-3 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-sm uppercase tracking-wider" style={{ color: '#D4AF37' }}>Layanan Pengiriman (JNE)</h2>

          {/* Customer shipping summary */}
          {form.province && form.city && form.district && (
            <div className="rounded-lg p-3 space-y-1.5 text-xs" style={{ background: 'rgba(0,0,0,0.25)' }} data-testid="shipping-summary">
              <div className="flex justify-between gap-3">
                <span style={{ color: '#A3B8AC' }}>Dikirim dari:</span>
                <span className="text-right" style={{ color: '#F8FAF9' }}>{originInfo.city || '—'}, {originInfo.district || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span style={{ color: '#A3B8AC' }}>Dikirim ke:</span>
                <span className="text-right" style={{ color: '#F8FAF9' }}>{form.city}, {form.district}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span style={{ color: '#A3B8AC' }}>Berat:</span>
                <span style={{ color: '#F8FAF9' }}>{fmt(totalWeightGrams)} gram</span>
              </div>
              <div className="flex justify-between gap-3">
                <span style={{ color: '#A3B8AC' }}>Kurir:</span>
                <span style={{ color: '#F8FAF9' }}>JNE</span>
              </div>
              {form.service && !rateMeta.requires_manual_override && (
                <div className="flex justify-between gap-3">
                  <span style={{ color: '#A3B8AC' }}>Service:</span>
                  <span style={{ color: '#F8FAF9' }}>{form.service}</span>
                </div>
              )}
              <div className="flex justify-between gap-3 pt-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ color: '#A3B8AC' }}>Ongkir:</span>
                <span className="font-semibold" style={{ color: '#D4AF37' }}>
                  {rateMeta.requires_manual_override ? 'Akan diatur admin' : `Rp ${fmt(form.shipping_cost)}`}
                </span>
              </div>
            </div>
          )}

          {!form.province || !form.city ? (
            <p className="text-xs" style={{ color: '#A3B8AC' }}>Pilih provinsi dan kota terlebih dahulu.</p>
          ) : loadingRates ? (
            <p className="text-xs" style={{ color: '#A3B8AC' }}>Mengambil tarif...</p>
          ) : rateMeta.requires_manual_override ? (
            <div className="rounded-lg p-3 text-xs space-y-2" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }} data-testid="manual-override-notice">
              <p style={{ color: '#F59E0B' }}>⚠ Tarif ongkir belum tersedia otomatis.</p>
              <p style={{ color: '#CFE0D5' }}>{rateMeta.message}</p>
              <p style={{ color: '#A3B8AC' }}>Lanjutkan submit — admin akan menghitung ongkir manual dan mengirim instruksi pembayaran ke Anda.</p>
            </div>
          ) : rates.length === 0 ? (
            <p className="text-xs" style={{ color: '#EF4444' }}>Tidak ada tarif tersedia untuk tujuan ini.</p>
          ) : (
            <div className="space-y-2" data-testid="rates-list">
              {rates.map((r) => (
                <button
                  type="button"
                  key={r.service}
                  onClick={() => setForm({ ...form, service: r.service, shipping_cost: r.cost })}
                  className="w-full flex items-center justify-between p-3 rounded-lg text-left"
                  style={{
                    background: form.service === r.service ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${form.service === r.service ? '#D4AF37' : 'rgba(255,255,255,0.05)'}`
                  }}
                  data-testid={`rate-option-${r.service}`}
                >
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#F8FAF9' }}>JNE {r.service}</p>
                    <p className="text-xs" style={{ color: '#A3B8AC' }}>{r.eta_days} hari</p>
                  </div>
                  <span className="text-sm font-semibold" style={{ color: '#D4AF37' }}>Rp {fmt(r.cost)}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Payment method */}
        <section className="rounded-2xl p-5 space-y-3 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-sm uppercase tracking-wider" style={{ color: '#D4AF37' }}>Payment</h2>
          <div className="grid grid-cols-4 gap-2">
            {PAYMENT_METHODS.map((pm) => {
              const Icon = pm.icon;
              const active = form.payment_method === pm.id;
              return (
                <button
                  key={pm.id}
                  type="button"
                  onClick={() => setForm({ ...form, payment_method: pm.id, shipping_cost: pm.id === 'pending_admin_quote' ? 0 : form.shipping_cost })}
                  className="rounded-lg p-3 flex flex-col items-center gap-1.5 text-center"
                  style={{
                    background: active ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${active ? '#D4AF37' : 'rgba(255,255,255,0.05)'}`
                  }}
                  data-testid={`pay-${pm.id}`}
                >
                  <Icon className="w-5 h-5" style={{ color: active ? '#D4AF37' : '#F8FAF9' }} />
                  <span className="text-sm font-semibold" style={{ color: active ? '#D4AF37' : '#F8FAF9' }}>{pm.label}</span>
                  <span className="text-[10px]" style={{ color: '#A3B8AC' }}>{pm.desc}</span>
                </button>
              );
            })}
          </div>
          {form.payment_method === 'coins' && (
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.25)' }}>
              <span className="text-sm" style={{ color: '#A3B8AC' }}>Coin balance</span>
              <span className="text-sm font-semibold" style={{ color: '#D4AF37' }}>{fmt(user?.coin_balance)} coins</span>
            </div>
          )}
          {(form.payment_method === 'qris' || form.payment_method === 'mandiri') && (
            <div>
              <p className="text-xs mb-2" style={{ color: '#A3B8AC' }}>
                <FileImage className="w-3 h-3 inline mr-1" />
                Upload payment proof after paying via {form.payment_method.toUpperCase()}.
              </p>
              <ImageUpload
                value={form.payment_proof_url}
                onChange={(v) => setForm({ ...form, payment_proof_url: v })}
                label="Payment proof"
                testId="payment-proof-upload"
                required
                userMode
              />
            </div>
          )}
        </section>

        {/* Notes */}
        <section className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <textarea placeholder="Notes for admin (optional)" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg px-3 py-2.5 outline-none" style={inputStyle} />
        </section>

        {/* Sticky submit */}
        <div className="sticky bottom-[68px] z-[55] -mx-6 px-6 py-3 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.95)', borderTop: '1px solid rgba(212,175,55,0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: '#A3B8AC' }}>Shipping cost</span>
            <span className="text-lg font-semibold" style={{ color: '#D4AF37' }}>{fmt(form.shipping_cost)} {form.payment_method === 'coins' ? 'coins' : 'IDR'}</span>
          </div>
          <button
            type="submit"
            disabled={submitting || (form.payment_method !== 'pending_admin_quote' && form.shipping_cost <= 0)}
            className="w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: '#D4AF37', color: '#080C0A' }}
            data-testid="submit-shipping-btn"
          >
            <Truck className="w-5 h-5" />
            {submitting ? 'Mengirim...' : (form.payment_method === 'pending_admin_quote' ? 'Kirim Permintaan ke Admin' : 'Submit Shipping Request')}
          </button>
        </div>
      </form>

      <MobileNav />
    </div>
  );
};
